import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { createLead } from "@/lib/api";
import { ArrowRight, Loader2 } from "lucide-react";

export default function LeadDialog({ open, onOpenChange, context = {}, onSuccess, title, subtitle, cta = "Get My Plan" }) {
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", country: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please enter your name and email.");
      return;
    }
    setLoading(true);
    try {
      const lead = await createLead({ ...form, ...context });
      toast.success("Thanks! Generating your results…");
      onOpenChange(false);
      if (onSuccess) onSuccess(lead);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "bg-white border-slate-200 rounded-lg h-11 focus-visible:ring-brand";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white rounded-2xl max-w-md" data-testid="lead-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-slate-900">{title || "Unlock Your Transformation Plan"}</DialogTitle>
        </DialogHeader>
        <p className="text-slate-500 text-sm -mt-1">{subtitle || "Tell us where to send your AI-generated website transformation plan."}</p>
        <form onSubmit={submit} className="space-y-3 mt-2">
          <Input data-testid="lead-name" placeholder="Full name*" value={form.name} onChange={set("name")} className={inputCls} />
          <Input data-testid="lead-email" type="email" placeholder="Work email*" value={form.email} onChange={set("email")} className={inputCls} />
          <div className="grid grid-cols-2 gap-3">
            <Input data-testid="lead-company" placeholder="Company" value={form.company} onChange={set("company")} className={inputCls} />
            <Input data-testid="lead-country" placeholder="Country" value={form.country} onChange={set("country")} className={inputCls} />
          </div>
          <Input data-testid="lead-phone" placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} className={inputCls} />
          <Textarea data-testid="lead-message" placeholder="What would you like to achieve? (optional)" value={form.message} onChange={set("message")} className="bg-white border-slate-200 rounded-lg min-h-[70px] focus-visible:ring-brand" />
          <button type="submit" disabled={loading} data-testid="lead-submit" className="btn-primary rounded-lg w-full py-3 flex items-center justify-center gap-2 disabled:opacity-60">
            {loading ? <Loader2 className="animate-spin" size={18} /> : <>{cta} <ArrowRight size={16} /></>}
          </button>
          <p className="text-[11px] text-slate-400 text-center">Privacy-first. We only collect what we need to help you.</p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
